import { Helmet } from 'react-helmet-async';
import { Box, Container, Grid, Typography, Card, CardContent, IconButton, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { 
  AutoAwesome as AutoAwesomeIcon,
  School as SchoolIcon,
  PlayArrow as PlayArrowIcon,
  Psychology as PsychologyIcon,
  Timeline as TimelineIcon,
  Group as GroupIcon,
  Security as SecurityIcon,
  Note as NoteIcon
} from '@mui/icons-material';
import { LandingLayout } from '../components/landing/LandingLayout';
import { Footer } from '../components/landing/Footer';

const features = [
  {
    title: 'AI Study Loop Generator',
    description: 'Generate comprehensive study loops for any topic instantly. Our AI creates carefully crafted questions and content to help you master any subject.',
    icon: <AutoAwesomeIcon sx={{ fontSize: 40 }} />,
    color: 'primary.main'
  },
  {
    title: 'Smart Note Taking',
    description: 'Take comprehensive notes and transform them into focused study loops using AI. Convert your lecture notes, research, or any text into effective study materials instantly.',
    icon: <NoteIcon sx={{ fontSize: 40 }} />,
    color: 'primary.dark'
  },
  {
    title: 'Custom Study Loops',
    description: 'Create your own study loops with flashcards or multiple choice questions. Perfect for exam prep, language learning, or any subject you want to master.',
    icon: <SchoolIcon sx={{ fontSize: 40 }} />,
    color: 'primary.light'
  },
  {
    title: 'Flexible Study Sessions',
    description: 'Study one or multiple loops in a single session. Mix and match different topics to create comprehensive study sessions that suit your needs.',
    icon: <PlayArrowIcon sx={{ fontSize: 40 }} />,
    color: 'primary.main'
  },
  {
    title: 'Smart Learning',
    description: 'Our system adapts to your performance, helping you focus on areas that need more attention while reinforcing what you already know.',
    icon: <PsychologyIcon sx={{ fontSize: 40 }} />,
    color: 'primary.dark'
  },
  {
    title: 'Progress Tracking',
    description: 'Monitor your learning journey with detailed analytics. Track your performance across different topics and identify areas for improvement.',
    icon: <TimelineIcon sx={{ fontSize: 40 }} />,
    color: 'primary.light'
  },
  {
    title: 'Collaborative Learning',
    description: 'Share study loops with classmates or study groups. Work together to master difficult topics and share knowledge.',
    icon: <GroupIcon sx={{ fontSize: 40 }} />,
    color: 'primary.main'
  },
  {
    title: 'Secure Platform',
    description: 'Your study data is protected with enterprise-grade security. Access your study loops safely from any device.',
    icon: <SecurityIcon sx={{ fontSize: 40 }} />,
    color: 'primary.dark'
  }
];

export const FeaturesPage = () => {
  const theme = useTheme();

  return (
    <LandingLayout>
      <Helmet>
        <title>BrainLoop Features - AI Flashcards, Study Loops & Progress Tracking</title>
        <meta name="description" content="Explore BrainLoop's features: AI-generated flashcard decks, smart study loops, spaced repetition, note-to-deck conversion, progress analytics, and collaborative learning." />
        <link rel="canonical" href="https://brainloop.app/features" />
        <meta property="og:title" content="BrainLoop Features - AI Flashcards, Study Loops & Progress Tracking" />
        <meta property="og:description" content="Explore BrainLoop's features: AI-generated flashcard decks, smart study loops, spaced repetition, note-to-deck conversion, and progress analytics." />
        <meta property="og:url" content="https://brainloop.app/features" />
      </Helmet>
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
              background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Powerful Features for Better Learning
            </Typography>
            <Typography variant="h5" color="text.secondary" sx={{ maxWidth: '800px', mx: 'auto' }}>
              Discover how BrainLoop's innovative features can transform your study experience
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: 'background.paper',
                    border: `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.1) : alpha(theme.palette.common.black, 0.07)}`,
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                    <IconButton
                      sx={{
                        mb: 2,
                        width: 62,
                        height: 62,
                        borderRadius: '50%',
                        bgcolor: 'transparent',
                        background: `linear-gradient(145deg, ${theme.palette.primary.light}, ${theme.palette.secondary.main})`,
                        boxShadow: [
                          '0 8px 20px rgba(99,102,241,0.45)',
                          '0 3px 8px rgba(0,0,0,0.15)',
                          'inset 0 2px 0 rgba(255,255,255,0.3)',
                          'inset 0 -2px 0 rgba(0,0,0,0.15)',
                        ].join(', '),
                        '&:hover': {
                          background: `linear-gradient(145deg, ${theme.palette.primary.main}, ${theme.palette.secondary.dark})`,
                          boxShadow: [
                            '0 10px 26px rgba(99,102,241,0.55)',
                            '0 4px 10px rgba(0,0,0,0.18)',
                            'inset 0 2px 0 rgba(255,255,255,0.28)',
                            'inset 0 -2px 0 rgba(0,0,0,0.18)',
                          ].join(', '),
                        },
                        color: 'primary.contrastText',
                      }}
                    >
                      {feature.icon}
                    </IconButton>
                    <Typography
                      variant="h6"
                      component="h3"
                      gutterBottom
                      sx={{ fontWeight: 700 }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
      <Footer />
    </LandingLayout>
  );
}; 